import {
  AppLocale,
  DEFAULT_LOCALE,
  LOCALE_STORAGE_KEY,
  localeLanguageTags,
} from "./app-locale";
import { readLocaleDetectionInput, resolveInitialLocale } from "./locale-detector";

type LocaleListener = () => void;

const listeners = new Set<LocaleListener>();

let currentLocale: AppLocale = DEFAULT_LOCALE;
let isInitialized = false;

/**
 * 현재 언어를 반환한다. 최초 호출 시 접속자 지역 기준으로 언어를 결정한다.
 * React 컴포넌트에서는 `useI18n()`을, 그 외 모듈에서는 이 함수 또는 `translate()`를 사용한다.
 */
export function getLocale(): AppLocale {
  if (!isInitialized) initializeLocale();
  return currentLocale;
}

/** 현재 언어의 BCP 47 태그(예: "ko-KR"). Intl 포맷터에 사용한다. */
export function getLanguageTag(): string {
  return localeLanguageTags[getLocale()];
}

/**
 * 저장된 설정 → 접속자 지역 → 기본 언어(한국어) 순으로 초기 언어를 확정한다.
 * 앱 진입 시 한 번 호출되며, 이후 호출은 무시된다.
 */
export function initializeLocale(): AppLocale {
  if (isInitialized) return currentLocale;
  isInitialized = true;

  currentLocale = resolveInitialLocale(readLocaleDetectionInput(readStoredLocale()));
  applyDocumentLanguage(currentLocale);
  return currentLocale;
}

/** 사용자가 선택한 언어를 적용하고 localStorage에 보관한다. */
export function setLocale(locale: AppLocale): void {
  if (!isInitialized) initializeLocale();
  if (locale === currentLocale) return;

  currentLocale = locale;
  writeStoredLocale(locale);
  applyDocumentLanguage(locale);
  listeners.forEach((listener) => listener());
}

/** `useSyncExternalStore` 구독용 함수 */
export function subscribeLocale(listener: LocaleListener): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

/** 테스트 전용: 모듈 상태를 초기화한다. */
export function resetLocaleStoreForTest(): void {
  currentLocale = DEFAULT_LOCALE;
  isInitialized = false;
  listeners.clear();
}

function readStoredLocale(): string | null {
  try {
    return window.localStorage.getItem(LOCALE_STORAGE_KEY);
  } catch {
    // 스토리지가 차단된 환경에서는 지역 기준 감지만 사용한다.
    return null;
  }
}

function writeStoredLocale(locale: AppLocale): void {
  try {
    window.localStorage.setItem(LOCALE_STORAGE_KEY, locale);
  } catch {
    // 스토리지가 차단된 환경에서는 현재 세션에만 적용한다.
  }
}

function applyDocumentLanguage(locale: AppLocale): void {
  if (typeof document === "undefined") return;
  document.documentElement.lang = localeLanguageTags[locale];
}
